import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { resolveUserTimezone } from "./timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export type DateRangePresetId =
  | "today"
  | "current_week"
  | "current_month"
  | "current_year"
  | "yesterday"
  | "last_week"
  | "last_month"
  | "last_year"
  | "last_15_days"
  | "last_30_days"
  | "last_60_days"
  | "last_90_days"
  | "next_7_days"
  | "fiscal_year";

export type DateRangePresetOption = {
  id: DateRangePresetId;
  label: string;
};

export const DATE_RANGE_PRESET_OPTIONS: DateRangePresetOption[] = [
  { id: "today", label: "Today" },
  { id: "current_week", label: "Current Week" },
  { id: "current_month", label: "Current Month" },
  { id: "current_year", label: "Current Year" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last_week", label: "Last Week" },
  { id: "last_month", label: "Last Month" },
  { id: "last_year", label: "Last Year" },
  { id: "last_15_days", label: "Last 15 Days" },
  { id: "last_30_days", label: "Last 30 Days" },
  { id: "last_60_days", label: "Last 60 Days" },
  { id: "last_90_days", label: "Last 90 Days" },
  { id: "fiscal_year", label: "Fiscal Year" },
];

/** Route screen presets: no fiscal year; includes upcoming week window. */
export const ROUTE_DATE_RANGE_PRESET_OPTIONS: DateRangePresetOption[] = [
  { id: "today", label: "Today" },
  { id: "current_week", label: "Current Week" },
  { id: "next_7_days", label: "Next 7 Days" },
  { id: "current_month", label: "Current Month" },
  { id: "current_year", label: "Current Year" },
  { id: "yesterday", label: "Yesterday" },
  { id: "last_week", label: "Last Week" },
  { id: "last_month", label: "Last Month" },
  { id: "last_year", label: "Last Year" },
  { id: "last_15_days", label: "Last 15 Days" },
  { id: "last_30_days", label: "Last 30 Days" },
  { id: "last_60_days", label: "Last 60 Days" },
  { id: "last_90_days", label: "Last 90 Days" },
];

export const ROUTE_DATE_RANGE_DEFAULT_PRESET: DateRangePresetId = "current_week";

export const CUSTOM_DATE_RANGE_PRESET = "custom" as const;

export type RouteDateRangePresetValue =
  | DateRangePresetId
  | typeof CUSTOM_DATE_RANGE_PRESET;

/** Python weekday: Monday = 0, Sunday = 6 (matches Odoo summary report presets). */
function pythonWeekday(date: Dayjs): number {
  return (date.day() + 6) % 7;
}

function todayInTimezone(tz: string): Dayjs {
  return dayjs().tz(resolveUserTimezone(tz)).startOf("day");
}

function lastDaysRange(today: Dayjs, days: number): { start: Dayjs; end: Dayjs } {
  return {
    start: today.subtract(days - 1, "day"),
    end: today,
  };
}

function fiscalYearRange(today: Dayjs, tz: string): { start: Dayjs; end: Dayjs } {
  const safeTz = resolveUserTimezone(tz);
  const year = today.year();
  const month = today.month() + 1;

  if (month >= 4) {
    return {
      start: dayjs.tz(`${year}-04-01`, safeTz).startOf("day"),
      end: dayjs.tz(`${year + 1}-03-31`, safeTz).startOf("day"),
    };
  }

  return {
    start: dayjs.tz(`${year - 1}-04-01`, safeTz).startOf("day"),
    end: dayjs.tz(`${year}-03-31`, safeTz).startOf("day"),
  };
}

export function getDateRangeForPreset(
  preset: DateRangePresetId,
  tz: string
): { start: Dayjs; end: Dayjs } {
  const today = todayInTimezone(tz);

  switch (preset) {
    case "today":
      return { start: today, end: today };

    case "yesterday": {
      const yesterday = today.subtract(1, "day");
      return { start: yesterday, end: yesterday };
    }

    case "current_week": {
      const start = today.subtract(pythonWeekday(today) + 1, "day");
      return { start, end: start.add(6, "day") };
    }

    case "last_week": {
      const start = today.subtract(pythonWeekday(today) + 8, "day");
      return { start, end: start.add(6, "day") };
    }

    case "current_month":
      return {
        start: today.startOf("month"),
        end: today.endOf("month").startOf("day"),
      };

    case "last_month": {
      const lastMonth = today.subtract(1, "month");
      return {
        start: lastMonth.startOf("month"),
        end: lastMonth.endOf("month").startOf("day"),
      };
    }

    case "current_year":
      return {
        start: today.startOf("year"),
        end: today.endOf("year").startOf("day"),
      };

    case "last_year": {
      const lastYear = today.subtract(1, "year");
      return {
        start: lastYear.startOf("year"),
        end: lastYear.endOf("year").startOf("day"),
      };
    }

    case "last_15_days":
      return lastDaysRange(today, 15);

    case "last_30_days":
      return lastDaysRange(today, 30);

    case "last_60_days":
      return lastDaysRange(today, 60);

    case "last_90_days":
      return lastDaysRange(today, 90);

    case "next_7_days":
      return { start: today, end: today.add(6, "day") };

    case "fiscal_year":
      return fiscalYearRange(today, tz);

    default:
      return { start: today, end: today };
  }
}
